import { Button, Input } from "@legal-500/legal500-design-system";
import type { RefCallback } from "react";
import { Link } from "react-router-dom";
import { ROUTES } from "../../../../routes";
import type { SidebarChoice, SidebarOption, SidebarSelection } from "../../pageSidebar";
import type { SidebarOptionsStatus } from "../../useSidebarOptions";
import styles from "./SidebarField.module.css";

type SidebarFieldProps = {
  name: string;
  options: SidebarOption[];
  optionsStatus: SidebarOptionsStatus;
  value: SidebarSelection;
  onChange: (value: SidebarSelection) => void;
  pageTitle: string;
  renameMenuId: string | null;
  error?: string;
  inputRef?: RefCallback<HTMLElement>;
};

function joinUnavailableMessage(
  optionsStatus: SidebarOptionsStatus,
  optionCount: number,
): string | null {
  if (optionsStatus === "loading") {
    return "Loading sidebars…";
  }
  if (optionsStatus === "failed") {
    return "Sidebars couldn't be loaded. Reload the page to try again.";
  }
  if (optionCount === 0) {
    return "No sidebars yet";
  }
  return null;
}

export function SidebarField({
  name,
  options,
  optionsStatus,
  value,
  onChange,
  pageTitle,
  renameMenuId,
  error,
  inputRef,
}: SidebarFieldProps) {
  const errorId = `${name}-error`;
  const newNameId = `${name}-new-name`;
  const newHintId = `${name}-new-hint`;
  const joinHintId = `${name}-join-hint`;
  const selectId = `${name}-menu`;
  const joinUnavailable = joinUnavailableMessage(optionsStatus, options.length);
  const chosenSidebar = options.find(option => option.menuId === value.menuId);
  const joining = value.choice === "join";
  const renaming = joining && value.rename !== null;
  const canRename = joining && value.menuId !== "" && value.menuId === renameMenuId;
  const focusSelect = joining && !renaming && joinUnavailable === null;
  const focusNewRadio = !focusSelect && !renaming && value.choice !== "new";

  function chooseAction(choice: SidebarChoice) {
    onChange({ ...value, choice, menuId: choice === "join" ? value.menuId : "", rename: null });
  }

  function chooseSidebar(menuId: string) {
    onChange({ ...value, choice: "join", menuId, rename: null });
  }

  return (
    <fieldset className={styles.sidebarField} aria-describedby={error ? errorId : undefined}>
      <legend className={styles.legend}>
        Sidebar <span className={styles.required}>*</span>
      </legend>

      <div className={styles.choice}>
        <label className={styles.choiceLabel}>
          <input
            ref={focusNewRadio ? inputRef : undefined}
            type="radio"
            className={styles.radio}
            name={name}
            value="new"
            checked={value.choice === "new"}
            onChange={() => chooseAction("new")}
            aria-labelledby={newNameId}
            aria-describedby={newHintId}
          />
          <span className={styles.choiceText}>
            <span id={newNameId} className={styles.choiceName}>
              Start a new sidebar for this page
            </span>
            <span id={newHintId} className={styles.hint}>
              Other pages can join it later.
            </span>
          </span>
        </label>

        {value.choice === "new" && (
          <div className={styles.nameField}>
            <Input
              ref={inputRef}
              id={`${name}-new-sidebar-name`}
              label="Sidebar name"
              value={value.newName ?? pageTitle}
              onChange={event => onChange({ ...value, newName: event.target.value })}
              aria-describedby={`${name}-new-sidebar-name-hint`}
              fullWidth
            />
            <span id={`${name}-new-sidebar-name-hint`} className={styles.hint}>
              Shown as the section heading above the sidebar, e.g. Trust Centre.
            </span>
          </div>
        )}
      </div>

      <div className={styles.choice}>
        <label className={styles.choiceLabel}>
          <input
            type="radio"
            className={styles.radio}
            name={name}
            value="join"
            checked={joining}
            onChange={() => chooseAction("join")}
            disabled={joinUnavailable !== null}
            aria-describedby={joinUnavailable ? joinHintId : undefined}
          />
          <span className={styles.choiceName}>Use the same sidebar as…</span>
        </label>

        {joinUnavailable ? (
          <span id={joinHintId} className={styles.hint}>
            {joinUnavailable}
          </span>
        ) : (
          <select
            ref={focusSelect ? inputRef : undefined}
            id={selectId}
            className={styles.select}
            aria-label="Sidebar to use"
            value={value.menuId}
            onChange={event => chooseSidebar(event.target.value)}
          >
            <option value="">Choose a sidebar</option>
            {value.menuId && !chosenSidebar && (
              <option value={value.menuId}>Current sidebar</option>
            )}
            {options.map(option => (
              <option key={option.menuId} value={option.menuId}>
                {option.label}
              </option>
            ))}
          </select>
        )}

        {joining && chosenSidebar && !renaming && (
          <div className={styles.nameField}>
            <p className={styles.currentName}>
              Sidebar name: <strong>{chosenSidebar.name}</strong>
            </p>
            {canRename ? (
              <div>
                <Button
                  type="button"
                  variant="outlined"
                  color="default"
                  onClick={() => onChange({ ...value, rename: chosenSidebar.name })}
                >
                  Rename
                </Button>
              </div>
            ) : (
              <span className={styles.hint}>You can rename it once this page is saved.</span>
            )}
          </div>
        )}

        {renaming && (
          <div className={styles.nameField}>
            <Input
              ref={inputRef}
              id={`${name}-rename`}
              label="Sidebar name"
              value={value.rename ?? ""}
              onChange={event => onChange({ ...value, rename: event.target.value })}
              aria-describedby={`${name}-rename-warning`}
              fullWidth
            />
            <span id={`${name}-rename-warning`} className={styles.renameWarning}>
              Renaming changes the heading on every page in this sidebar.
            </span>
            <div>
              <Button
                type="button"
                variant="outlined"
                color="default"
                onClick={() => onChange({ ...value, rename: null })}
              >
                Keep current name
              </Button>
            </div>
          </div>
        )}
      </div>

      <Link className={styles.manageLink} to={ROUTES.cms.menus} target="_blank" rel="noreferrer">
        Reorder or rename sidebar links
        <span className="sr-only"> (opens in a new tab)</span>
      </Link>

      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}
